<script lang="ts">
	import '../app.css';
	import Header from '$lib/components/layout/Header.svelte';
	import Footer from '$lib/components/layout/Footer.svelte';
	import CookieBanner from '$lib/components/CookieBanner.svelte';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';
	import { onNavigate } from '$app/navigation';
	import { metadata } from '$lib/content/data/metadata';
	import { storeUtmParams } from '$lib/utils/utm';

	// Import local fonts
	import '@fontsource/bricolage-grotesque';
	import '@fontsource/dm-sans';

	let { children, data } = $props();

	// Get current pathname for active state - use $derived to react to changes
	let pathname = $derived(data?.url ?? '/');

	// Parse and store UTM params whenever the URL search params change
	$effect(() => {
		const url = $page.url;
		if (url?.searchParams?.toString()) {
			storeUtmParams(url);
		}
	});

	// Trigger Native Browser View Transitions for fluid page navigation
	onNavigate((navigation) => {
		if (!browser || !document.startViewTransition) return;

		return new Promise((resolve) => {
			document.startViewTransition!(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});
</script>

<!-- Preconnect hints moved to app.html for earlier loading -->

<svelte:head>
	<!-- Hreflang tags for regional targeting (SEA focus) -->
	<link rel="alternate" hreflang="x-default" href="{metadata.company.website.replace(/\/$/, '')}{pathname.replace(/\/$/, '')}" />
	<link rel="alternate" hreflang="en-my" href="{metadata.company.website.replace(/\/$/, '')}{pathname.replace(/\/$/, '')}" />
	<link rel="alternate" hreflang="en-sg" href="{metadata.company.website.replace(/\/$/, '')}{pathname.replace(/\/$/, '')}" />
	<link rel="alternate" hreflang="en-in" href="{metadata.company.website.replace(/\/$/, '')}{pathname.replace(/\/$/, '')}" />
	<link rel="alternate" hreflang="en-us" href="{metadata.company.website.replace(/\/$/, '')}{pathname.replace(/\/$/, '')}" />

	<!-- Global SEO Fallbacks — og:image/twitter:image are intentionally NOT
	     set here: layout head renders before page head, and scrapers use the
	     first tag, so a layout default would shadow every page-specific image.
	     Each page declares its own (ai-lab pages fall back to the brand card). -->
	<meta property="og:site_name" content={metadata.company.name} />
	<meta property="og:type" content="website" />
	<meta name="twitter:card" content="summary_large_image" />

	<!-- Global JSON-LD Schema (Organization) -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "Organization",
		"name": metadata.company.name,
		"url": metadata.company.website,
		"logo": `${metadata.company.website}/brand/icon.png`,
		"sameAs": [
			metadata.social.linkedin,
			metadata.social.twitter,
			metadata.social.github,
			metadata.social.youtube
		]
	})}</` + `script>`}
</svelte:head>

<div class="min-h-screen bg-ak-deep text-ak-text-primary font-body">
	<!-- Skip link for accessibility - visible on focus -->
	<a
		href="#main-content"
		class="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:bg-ak-wintergreen focus:text-ak-deep focus:px-5 focus:py-3 focus:rounded-lg focus:font-semibold focus:shadow-lg focus:shadow-ak-wintergreen/30 focus:outline-none focus:ring-2 focus:ring-ak-deep"
	>
		Skip to main content
	</a>

	<Header {pathname} />

	{@render children()}

	<Footer />
	<CookieBanner />
</div>
